import type * as React from "react";

import { AgentGlyphIcon } from "@/components/icons";
import { initial, initials, slotOf, type Person } from "@/lib/people";
import { cn } from "@/lib/utils";

/*
 * The people disc, the same face the review panel draws on the page.
 * A member is a round disc in their slot colour (FNV-1a of the user id) with
 * initials in the disc ink. A guest has no account id, so the disc is
 * neutral. An agent is a square with its client glyph. Never a photo, so a
 * person reads the same here and in the SDK.
 */
function Avatar({
  className,
  letters = "two",
  person,
  size = "sm",
  style,
  ...props
}: React.ComponentProps<"span"> & {
  /** Rows and messages use two initials; pins use one. */
  readonly letters?: "one" | "two";
  readonly person: Person;
  /** sm is 20px, default is 28px. */
  readonly size?: "sm" | "default";
}) {
  const slot = person.kind === "member" ? slotOf(person.id) : null;
  return (
    <span
      aria-hidden="true"
      data-kind={person.kind}
      data-size={size}
      data-slot="avatar"
      className={cn("avatar", className)}
      style={
        slot === null
          ? style
          : ({ ...style, "--slot": `var(--p${slot})` } as React.CSSProperties)
      }
      {...props}
    >
      {person.kind === "agent" ? (
        <AgentGlyphIcon glyph={person.glyph} />
      ) : letters === "one" ? (
        initial(person.name)
      ) : (
        initials(person.name)
      )}
    </span>
  );
}

export { Avatar };
